/**
 * ProgramDetail — public detail page + registration form for a single
 * symposium or stage. Handles free (instant confirm) and paid (SumUp) flows.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { pickLocalized } from "@/i18n/translations";
import Ornament from "@/components/Ornament";

const CONTRIBUTION_LABELS = {
  auditor: { fr: "Auditeur", en: "Attendee" },
  speaker: { fr: "Intervenant avec communication", en: "Speaker with talk" },
  poster: { fr: "Présentation d'un poster", en: "Poster presentation" },
};
const DIETARY = ["omnivore", "vegetarien", "sans_gluten", "casher", "halal", "autre"];
const DIETARY_LABELS = {
  omnivore: { fr: "Omnivore", en: "Omnivore" },
  vegetarien: { fr: "Végétarien", en: "Vegetarian" },
  sans_gluten: { fr: "Sans gluten", en: "Gluten-free" },
  casher: { fr: "Casher", en: "Kosher" },
  halal: { fr: "Halal", en: "Halal" },
  autre: { fr: "Autre", en: "Other" },
};

const emptyForm = () => ({
  first_name: "", last_name: "", email: "", phone: "",
  country: "FR", city: "",
  institution: "", function: "",
  contribution: "auditor",
  talk_title: "", talk_abstract: "",
  dietary: "omnivore",
  lodging: "none",
  arrival_date: "", departure_date: "",
  notes: "",
  want_lodging: false, want_meals: false,
});

export default function ProgramDetail() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const l = locale === "fr" ? "fr" : "en";
  const [prog, setProg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [freeOk, setFreeOk] = useState(false);

  useEffect(() => {
    api.get(`/programs/${slug}`)
      .then((r) => setProg(r.data))
      .catch(() => setProg(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const total = useMemo(() => {
    if (!prog) return 0;
    let t = Number(prog.price_registration_eur) || 0;
    if (form.want_lodging) t += Number(prog.price_lodging_eur) || 0;
    if (form.want_meals) t += Number(prog.price_meals_eur) || 0;
    return Math.round(t * 100) / 100;
  }, [prog, form.want_lodging, form.want_meals]);

  const update = (k) => (e) => {
    const v = e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  if (loading) return <div className="p-16 text-center italic" style={{ color: "var(--muted)" }}>…</div>;
  if (!prog) return <div className="p-16 text-center" style={{ color: "var(--cinnabar)" }}>404</div>;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const r = await api.post(`/programs/${slug}/register`, form);
      if (r.data.url) {
        window.location.assign(r.data.url); // → SumUp hosted checkout
      } else if (r.data.free) {
        setFreeOk(true);
      }
    } catch (err) {
      setMsg(err?.response?.data?.detail || (l === "fr" ? "Erreur d'inscription." : "Registration error."));
    } finally {
      setBusy(false);
    }
  };

  const fmtDates = (() => {
    try {
      const opts = { day: "numeric", month: "long", year: "numeric" };
      const s = new Date(prog.start_date).toLocaleDateString(l === "fr" ? "fr-FR" : "en-GB", opts);
      const e = new Date(prog.end_date).toLocaleDateString(l === "fr" ? "fr-FR" : "en-GB", opts);
      return l === "fr" ? `Du ${s} au ${e}` : `From ${s} to ${e}`;
    } catch { return prog.start_date; }
  })();

  return (
    <div data-testid={`program-detail-${prog.slug}`} style={{ background: "var(--parchment)" }}>
      <section className="py-14 px-6 text-center border-b" style={{ borderColor: "var(--gold)" }}>
        <Ornament />
        <div className="font-caps text-xs mt-4" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>
          {fmtDates}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl mt-2 max-w-3xl mx-auto" style={{ color: "var(--ink)" }}>
          {pickLocalized(prog.title, locale)}
        </h1>
        {prog.subtitle && (
          <p className="font-body italic text-lg mt-3 max-w-2xl mx-auto" style={{ color: "var(--muted)" }}>
            {pickLocalized(prog.subtitle, locale)}
          </p>
        )}
        <p className="font-body text-sm mt-3" style={{ color: "var(--muted)" }}>
          📍 {pickLocalized(prog.location, locale)}
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-12">
        <p className="font-body text-base leading-relaxed whitespace-pre-line" style={{ color: "var(--ink)" }}>
          {pickLocalized(prog.description, locale)}
        </p>
        {prog.pricing_notes && pickLocalized(prog.pricing_notes, locale) && (
          <div className="mt-8 border-l-4 pl-4 py-2 font-body italic text-sm" style={{ borderColor: "var(--gold)", color: "var(--muted)" }}>
            {pickLocalized(prog.pricing_notes, locale)}
          </div>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-16">
        {freeOk ? (
          <div className="bg-white border p-8 text-center" style={{ borderColor: "var(--gold)" }} data-testid="reg-success-free">
            <h2 className="font-heading text-2xl mb-3" style={{ color: "var(--ink)" }}>
              {l === "fr" ? "Inscription enregistrée" : "Registration confirmed"}
            </h2>
            <p className="font-body" style={{ color: "var(--muted)" }}>
              {l === "fr"
                ? "Nous prenons contact avec vous prochainement pour préciser les modalités."
                : "We'll reach out to you soon with the practical details."}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border p-8 space-y-4" style={{ borderColor: "var(--border)" }} data-testid="reg-form">
            <h2 className="font-heading text-2xl mb-3" style={{ color: "var(--ink)" }}>
              {l === "fr" ? "S'inscrire" : "Register"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label={l === "fr" ? "Prénom" : "First name"} required value={form.first_name} onChange={update("first_name")} testid="reg-first-name" />
              <Input label={l === "fr" ? "Nom" : "Last name"} required value={form.last_name} onChange={update("last_name")} testid="reg-last-name" />
              <Input label="Email" type="email" required value={form.email} onChange={update("email")} testid="reg-email" />
              <Input label={l === "fr" ? "Téléphone" : "Phone"} value={form.phone} onChange={update("phone")} testid="reg-phone" />
              <Input label={l === "fr" ? "Pays" : "Country"} value={form.country} onChange={update("country")} testid="reg-country" />
              <Input label={l === "fr" ? "Ville" : "City"} value={form.city} onChange={update("city")} testid="reg-city" />
              <Input label={l === "fr" ? "Institution / Congrégation" : "Institution / Congregation"} value={form.institution} onChange={update("institution")} testid="reg-institution" />
              <Input label={l === "fr" ? "Fonction / Titre" : "Position / Title"} value={form.function} onChange={update("function")} testid="reg-function" />
            </div>

            <Select label={l === "fr" ? "Contribution" : "Contribution"} value={form.contribution} onChange={update("contribution")}
              options={Object.entries(CONTRIBUTION_LABELS).map(([k, v]) => [k, v[l]])} testid="reg-contribution" />

            {form.contribution !== "auditor" && (
              <>
                <Input label={l === "fr" ? "Titre de la communication" : "Talk title"} value={form.talk_title} onChange={update("talk_title")} testid="reg-talk-title" />
                <Textarea label={l === "fr" ? "Résumé (500 mots max)" : "Abstract (max 500 words)"} value={form.talk_abstract} onChange={update("talk_abstract")} rows={4} testid="reg-talk-abstract" />
              </>
            )}

            <Select label={l === "fr" ? "Régime alimentaire" : "Dietary preference"} value={form.dietary} onChange={update("dietary")}
              options={DIETARY.map((k) => [k, DIETARY_LABELS[k][l]])} testid="reg-dietary" />

            {(prog.price_lodging_eur > 0 || prog.price_meals_eur > 0) && (
              <div className="border p-4 space-y-3" style={{ borderColor: "var(--border)", background: "var(--parchment)" }}>
                <div className="font-caps text-xs" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>
                  {l === "fr" ? "OPTIONS PAYANTES" : "PAID OPTIONS"}
                </div>
                {prog.price_lodging_eur > 0 && (
                  <label className="flex items-center gap-3 font-body">
                    <input data-testid="reg-lodging" type="checkbox" checked={form.want_lodging} onChange={update("want_lodging")} />
                    <span>
                      {l === "fr" ? "Hébergement sur place" : "On-site lodging"}
                      <span style={{ color: "var(--muted)" }}> — {prog.price_lodging_eur} €</span>
                    </span>
                  </label>
                )}
                {prog.price_meals_eur > 0 && (
                  <label className="flex items-center gap-3 font-body">
                    <input data-testid="reg-meals" type="checkbox" checked={form.want_meals} onChange={update("want_meals")} />
                    <span>
                      {l === "fr" ? "Repas (pension complète)" : "Meals (full board)"}
                      <span style={{ color: "var(--muted)" }}> — {prog.price_meals_eur} €</span>
                    </span>
                  </label>
                )}
              </div>
            )}

            <Textarea label={l === "fr" ? "Commentaires libres" : "Comments"} value={form.notes} onChange={update("notes")} rows={3} testid="reg-notes" />

            <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: "var(--border)" }}>
              <div>
                <div className="font-caps text-xs" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>Total</div>
                <div className="font-heading text-2xl" style={{ color: "var(--cinnabar)" }} data-testid="reg-total">
                  {total.toFixed(2)} €
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="reg-submit"
                className="btn btn-primary"
              >
                {busy ? "…" : total > 0
                  ? (l === "fr" ? "Payer et s'inscrire" : "Pay and register")
                  : (l === "fr" ? "Valider mon inscription" : "Confirm registration")}
              </button>
            </div>
            {msg && <p className="text-sm" style={{ color: "var(--cinnabar)" }} data-testid="reg-error">{msg}</p>}
          </form>
        )}
      </section>
    </div>
  );
}

function Input({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs font-body uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>{label}</span>
      <input data-testid={testid} className="w-full px-3 py-2 border font-body" style={{ borderColor: "var(--border)" }} {...rest} />
    </label>
  );
}
function Textarea({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs font-body uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>{label}</span>
      <textarea data-testid={testid} className="w-full px-3 py-2 border font-body" style={{ borderColor: "var(--border)" }} {...rest} />
    </label>
  );
}
function Select({ label, options, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs font-body uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>{label}</span>
      <select data-testid={testid} className="w-full px-3 py-2 border font-body bg-white" style={{ borderColor: "var(--border)" }} {...rest}>
        {options.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
      </select>
    </label>
  );
}
